import type { ComponentProps, ReactNode } from "react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  SurfaceProvider,
  useElevatedSurfaceClasses,
} from "~/lib/surface-context";
import { cn } from "~/lib/utils";

interface SectionCardProps extends Omit<
  ComponentProps<typeof Card>,
  "surface" | "title"
> {
  action?: ReactNode;
  contentClassName?: string;
  description?: ReactNode;
  headerClassName?: string;
  title?: ReactNode;
  variant?: "card" | "flat";
}

export function SectionCard({
  action,
  children,
  className,
  contentClassName,
  description,
  headerClassName,
  title,
  variant = "card",
  ...props
}: SectionCardProps) {
  const sectionSurface = useElevatedSurfaceClasses(1, 2);
  const card = (
    <Card
      className={cn(
        variant === "card" && "page-surface-panel rounded-lg",
        variant === "flat" &&
          "overflow-visible rounded-none bg-transparent py-0 ring-0 shadow-none [border:none]",
        className,
      )}
      surface={false}
      {...props}
    >
      {title || description || action ? (
        <CardHeader
          className={cn(
            "gap-1.5",
            variant === "flat" && "px-0",
            headerClassName,
          )}
        >
          {title ? <CardTitle>{title}</CardTitle> : null}
          {description ? (
            <CardDescription>{description}</CardDescription>
          ) : null}
          {action ? <CardAction>{action}</CardAction> : null}
        </CardHeader>
      ) : null}
      <CardContent
        className={cn(
          "space-y-4",
          variant === "flat" && "px-0",
          contentClassName,
        )}
      >
        {children}
      </CardContent>
    </Card>
  );

  if (variant === "flat") {
    return card;
  }

  return <SurfaceProvider value={sectionSurface.level}>{card}</SurfaceProvider>;
}
